import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AuthBusinessProfilesService } from '../../services/auth-business-profiles.service';
import { AuthService } from '../../services/auth.service';
import { AuthApiActions } from '../actions/auth-api.actions';
import { AuthBusinessProfilesApiActions } from '../actions/auth-business-profiles-api.actions';
import { AuthBusinessProfilesPageActions } from '../actions/auth-business-profiles-page.actions';

@Injectable()
export class AuthBusinessProfilesEffects {
  private actions$: Actions = inject(Actions);
  private authService: AuthService = inject(AuthService);
  private businessProfileService: AuthBusinessProfilesService = inject(AuthBusinessProfilesService);
  private router: Router = inject(Router);
  list$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthBusinessProfilesPageActions.loadBusinessProfiles),
      switchMap(() =>
        this.authService.loadAccount().pipe(
          map((account) => AuthBusinessProfilesApiActions.loadBusinessProfilesSuccess({ businessProfiles: account.businessProfiles })),
          catchError((error) => of(AuthApiActions.signInFailure({ error })))
        )
      )
    )
  );
  selectProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthBusinessProfilesPageActions.selectBusinessProfile),
      switchMap((action) =>
        this.businessProfileService.selectBusinessProfile(action.selectedBusinessProfile).pipe(
          map((selectedBusinessProfile) =>
            AuthBusinessProfilesApiActions.selectBusinessProfileSuccess({
              selectedBusinessProfile,
              withRedirect: action.withRedirect
            })
          ),
          catchError((error) => of(AuthBusinessProfilesApiActions.selectBusinessProfileFailure({ error })))
        )
      )
    )
  );
  selectBusinessProfileSuccessThenLoadPerimeterEntities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthBusinessProfilesApiActions.selectBusinessProfileSuccess),
      switchMap(() =>
        this.businessProfileService.getPerimeterEntities().pipe(
          map((entities) => AuthBusinessProfilesApiActions.loadPerimeterEntitiesSuccess({ entities })),
          catchError((error) => of(AuthBusinessProfilesApiActions.loadPerimeterEntitiesFailure({ error })))
        )
      )
    )
  );
  selectBusinessProfileSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthBusinessProfilesApiActions.selectBusinessProfileSuccess),
      switchMap((action) =>
        this.businessProfileService.getEntityById(action.selectedBusinessProfile.entityId).pipe(
          map((selectedEntity) => AuthBusinessProfilesApiActions.loadSelectedEntityForSessionSuccess({ selectedEntity })),
          catchError((error) => of(AuthBusinessProfilesApiActions.loadSelectedEntityForSessionFailure({ error })))
        )
      )
    )
  );
  navigateToHome$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthBusinessProfilesApiActions.selectBusinessProfileSuccess),
        map((action) => {
          if (action.withRedirect) {
            this.router.navigate(['/home']);
          }
        })
      ),
    { dispatch: false }
  );
}
